/**
 * Discreet floating links to the Privacy Policy + cookies disclosure
 * + Terms of Service.
 *
 * PIPA Art. 30 requires the privacy policy to be "easy to find" from
 * every page. The cookies link points at the §10-2 anchor (자동수집
 * 장치 disclosure) — added in #143's verification pass and surfaced
 * here per the #74 audit conclusion (only essential cookies; no
 * banner needed, but a permanent link is the minimum hygiene). The
 * Terms link (issue #425 item 2) is the contractual sibling — same
 * "easy to find from every page" posture as the privacy disclosure
 * since the ToS is the operative agreement the Institution accepts
 * at admin first-login.
 *
 * Mounted at app root so the links appear alongside the language /
 * theme switchers regardless of which surface the user is on.
 *
 * Hides itself when the user is already on the privacy page or the
 * terms page — surfacing these links from the document itself is
 * noise.
 */
import { Link, useLocation } from 'react-router-dom'
import { useLang } from '../i18n'
import s from './PrivacyFooter.module.css'

export default function PrivacyFooter() {
  const location = useLocation()
  const lang = useLang()

  if (
    location.pathname.startsWith('/privacy') ||
    location.pathname.startsWith('/terms')
  ) return null

  // Match the policy view to the user's app locale: Korean app users
  // get the Korean policy by default, English app users get
  // ?lang=en. Each document page has its own toggle for users who
  // want to switch.
  const privacyHref = lang === 'en' ? '/privacy?lang=en' : '/privacy'
  const cookiesHref = lang === 'en' ? '/privacy?lang=en#cookies' : '/privacy#cookies'
  const termsHref = lang === 'en' ? '/terms?lang=en' : '/terms'
  const privacyLabel = lang === 'en' ? 'Privacy' : '개인정보처리방침'
  const cookiesLabel = lang === 'en' ? 'Cookies' : '쿠키'
  const termsLabel = lang === 'en' ? 'Terms' : '이용약관'

  return (
    <div className={s.privacyFooterLinks}>
      <Link
        to={privacyHref}
        className={s.privacyFooterLink}
        aria-label={lang === 'en' ? 'Privacy policy' : '개인정보처리방침'}
      >
        {privacyLabel}
      </Link>
      <Link
        to={cookiesHref}
        className={s.privacyFooterLink}
        aria-label={lang === 'en' ? 'Cookies disclosure' : '쿠키 안내'}
      >
        {cookiesLabel}
      </Link>
      <Link
        to={termsHref}
        className={s.privacyFooterLink}
        aria-label={lang === 'en' ? 'Terms of service' : '이용약관'}
      >
        {termsLabel}
      </Link>
    </div>
  )
}
